<template>
  <div class="glossary-text">
    <sx-html :text="textParts" />
    <sc-flex v-if="priorYearTag">
      <sc-label type="info"
        >{{ $vuetify.breakpoint.smAndUp ? 'You had this in' : 'From' }}
        {{ questionnaireYear - 1 }}</sc-label
      >
    </sc-flex>
  </div>
</template>

<script>
import SxHtml from '@/components/sx/SxHtml';

export default {
  name: 'GlossaryTextHighlight',
  components: {
    SxHtml,
  },
  props: {
    text: {
      type: String,
      default: '',
    },
    firmGlossary: {
      type: [Object, Array],
      default: null,
    },
    priorYearTag: {
      type: Boolean,
      default: false,
    },
    questionnaireYear: {
      type: Number,
      default: 2022,
    },
    isSmall: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    textParts() {
      // Without glossary, we just render everything as is
      if (!this.hasGlossary) return this.text;

      // With glossary
      let textWithGlossary = this.text;

      // Replace glossary keywords with their ID placeholders, but avoid replacing inside HTML tags
      for (const glossary of this.firmGlossary) {
        textWithGlossary = textWithGlossary.replaceAll(
          new RegExp(
            `(?<!<[^>]*)(\\b${glossary.key_words}\\b)(?![^<]*>)`,
            'gi',
          ),
          `#${glossary.id}`,
        );
      }

      // Replace glossary ID placeholders with HTML content
      for (const glossary of this.firmGlossary) {
        textWithGlossary = textWithGlossary.replaceAll(
          new RegExp(`#${glossary.id}`, 'g'),
          `
            <span class="glossary-text__glossary">
              ${glossary.key_words}
              <span class="glossary-text__glossary-card">
                ${this.removeBlockSizedHtml(glossary.description)}
              </span>
            </span>
          `,
        );
      }

      return textWithGlossary;
    },
    hasGlossary() {
      return this.firmGlossary && this.firmGlossary.length;
    },
  },

  methods: {
    // NOTE @chris In order to make wyswyg editor work AND support wyswyg glossaries, we have to strip block sized element out of descriptions because they brake the paragraph tag before their content could render fully
    removeBlockSizedHtml(input) {
      const blocksRemoved = input.replace(
        /<\/?(div|p|h[1-6]|blockquote|pre|ol|ul|li|dl|dt|dd|hr|table|thead|tbody|tfoot|tr|th|td)[^>]*>/gi,
        '',
      );
      const spacesAdded = blocksRemoved.replaceAll('\n', '<br/>');

      return spacesAdded;
    },
  },
};
</script>

<!-- NOTE @chris Important to keep this unscoped to be able to reach elements downstream (because of vuetify overrides) -->
<style>
.glossary-text {
  position: relative;

  display: block;
}

.glossary-text p {
  margin: 0 !important;
}

.glossary-text__glossary {
  display: inline-flex;

  font-weight: bold;
}

.glossary-text__glossary:hover .glossary-text__glossary-card,
.glossary-text__glossary-card:hover {
  display: block;
}

.glossary-text__glossary-card {
  position: absolute;
  z-index: 100;

  display: none;
  height: auto;
  max-height: 380px;
  min-width: 440px;
  overflow: auto;
  padding: 12px;
  width: auto;

  animation: glossaryFadeIn ease-in-out 120ms 1 forwards;
  background-color: var(--gray-0);
  border-radius: 8px;
  box-shadow: 0px -2px 12px -2px rgba(16, 21, 24, 0.08),
    0px 12px 16px -4px rgba(16, 21, 24, 0.12);

  font-weight: normal !important;
}

@keyframes glossaryFadeIn {
  from {
    opacity: 0;
    transform: translateY(-12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
